/**
 * MediaManager.tsx - Media Upload and Management Component
 *
 * This component provides comprehensive media management for fundraisers
 * including upload, editing, reordering, and organization of images and videos.
 *
 * Key Features:
 * - Multi-file upload with drag-and-drop support
 * - Image and video support with format validation
 * - Drag-and-drop reordering with visual feedback
 * - Inline editing of titles and descriptions
 * - Media preview with editing options
 * - AI-powered description generation
 * - Bulk operations and management
 *
 * Media Operations:
 * - Upload: Drag-and-drop or click to upload files
 * - Edit: Modify titles, descriptions, and metadata
 * - Reorder: Drag-and-drop to change display order
 * - Delete: Remove media with confirmation
 * - Preview: View media in context
 *
 * AI Integration:
 * - Automatic description generation for images
 * - Context-aware descriptions based on fundraiser
 * - OpenAI service integration for content generation
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, drag-and-drop, media editing modal, AI service
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { X, GripVertical, Plus, Edit2 } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { EditMediaModal } from "../media-editor/EditMediaModal";
import { MediaType } from "../../enums";
import { MediaModel } from "../../models/GalleryModels";

const MediaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  position: relative;
`;

const MediaItem = styled.div<{ isDragging: boolean; isDropTarget: boolean }>`
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid ${(props) => (props.isDropTarget ? "#3b82f6" : "#e2e8f0")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.isDragging ? 0.7 : 1)};
  transform: ${(props) => {
    if (props.isDragging) return "rotate(3deg) scale(1.05)";
    if (props.isDropTarget) return "scale(1.02)";
    return "scale(1)";
  }};
  box-shadow: ${(props) => {
    if (props.isDragging) return "0 12px 24px rgba(0, 0, 0, 0.2)";
    return "0 2px 8px rgba(0, 0, 0, 0.05)";
  }};
  z-index: ${(props) => (props.isDragging ? 1000 : 1)};

  &:hover {
    border-color: #3b82f6;
    transform: ${(props) =>
      props.isDragging
        ? "rotate(3deg) scale(1.05)"
        : "translateY(-2px) scale(1.02)"};
    box-shadow: ${(props) =>
      props.isDragging
        ? "0 12px 24px rgba(0, 0, 0, 0.2)"
        : "0 8px 16px rgba(0, 0, 0, 0.12)"};

    .hover-overlay {
      opacity: 1;
      visibility: visible;
    }
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DropZone = styled.div<{
  isActive: boolean;
  position: "before" | "after";
}>`
  position: absolute;
  ${(props) => (props.position === "before" ? "left: -8px;" : "right: -8px;")}
  top: 0;
  bottom: 0;
  width: 4px;
  background: ${(props) =>
    props.isActive
      ? "linear-gradient(180deg, #3b82f6, #1d4ed8)"
      : "transparent"};
  border-radius: 2px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  transform: ${(props) => (props.isActive ? "scaleY(1)" : "scaleY(0.3)")};
  box-shadow: ${(props) =>
    props.isActive ? "0 0 8px rgba(59, 130, 246, 0.6)" : "none"};
  z-index: 10;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
`;

const HoverActions = styled.div`
  display: flex;
  gap: 12px;
`;

const HoverButton = styled.button<{ variant?: "danger" }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #ffffff;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    color: #ffffff;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 168px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MediaVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MediaPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #64748b;
`;

const MediaInfo = styled.div`
  padding: 12px;
  border-top: 1px solid #e2e8f0;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

const ModalMediaPreview = styled.div`
  width: 100%;
  height: 200px;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalMediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ModalMediaVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  padding-right: ${(props) => (props.value ? "40px" : "16px")};
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const TextAreaContainer = styled.div`
  position: relative;
  display: flex;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  padding-right: ${(props) => (props.value ? "40px" : "16px")};
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearButton = styled.button<{ visible: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  opacity: 0;

  ${InputContainer}:hover &,
  ${TextAreaContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.9);
  }
`;

const TextAreaClearButton = styled(ClearButton)`
  top: 24px;
  transform: translateY(0);

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const RotateMediaSection = styled.div<{ visible: boolean }>`
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`;

const RotateCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const RotateLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  user-select: none;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "primary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #3b82f6;
    color: #ffffff;

    &:hover {
      background: #2563eb;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const MediaTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  min-height: 20px;
  margin-bottom: 4px;
`;

const MediaDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AddMediaButton = styled.button<{ hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  border: 2px dashed ${(props) => (props.hasError ? "#dc2626" : "#cbd5e1")};
  border-radius: 12px;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props) => (props.hasError ? "#dc2626" : "#64748b")};
  min-height: 180px;

  &:hover {
    border-color: ${(props) => (props.hasError ? "#b91c1c" : "#3b82f6")};
    color: ${(props) => (props.hasError ? "#b91c1c" : "#3b82f6")};
    background: ${(props) => (props.hasError ? "#fef2f2" : "#f8fafc")};
  }
`;

const AddMediaText = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const AddMediaSubtext = styled.div`
  font-size: 14px;
  color: #94a3b8;
`;

const FileInput = styled.input`
  display: none;
`;

const URLInputContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const URLInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const AddURLButton = styled.button`
  padding: 12px 24px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

interface MediaManagerProps {
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
  hasError?: boolean;
}

export const MediaManager: React.FC<MediaManagerProps> = observer(
  ({ onKeyDown, hasError }) => {
    const { builderData } = fundraiserStore;
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingMedia, setEditingMedia] = useState<MediaModel | null>(null);
    const [modalTitle, setModalTitle] = useState("");
    const [modalDescription, setModalDescription] = useState("");
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverItem, setDragOverItem] = useState<string | null>(null);
    const [dropPosition, setDropPosition] = useState<"before" | "after" | null>(
      null
    );

    const mediaItems = builderData.mediaItems || [];
    const rotateMedia = builderData.rotateMedia || false;
    console.log("Media Items:", mediaItems);
  
    const handleFileUpload = (files: FileList | null) => {
      if (!files) return;

      Array.from(files).forEach((file) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const newItem: MediaModel = new MediaModel({
              mediaId: Date.now().toString() + Math.random(),
              type: MediaType.Image,
              title: "", // Keep title blank, don't use filename
              description: "",
              fullURL: e.target?.result as string, // Add url field
            });
            fundraiserStore.updateBuilderField("mediaItems", [
              ...mediaItems,
              newItem,
            ]);
          };
          reader.readAsDataURL(file);
        }
      });
    };

    const removeMedia = (id: string) => {
      const updated = mediaItems.filter((item) => item.mediaId !== id);
      fundraiserStore.updateBuilderField("mediaItems", updated);
    };

    const updateMediaItem = (id: string, data: MediaModel) => {
      const updated = mediaItems.map((item) =>
        item.mediaId === id ? new MediaModel({ ...item, ...data }) : item
      );
      fundraiserStore.updateBuilderField("mediaItems", updated);
    };

    const handleEditClick = (media: MediaModel) => {
      setEditingMedia(media);
      setShowEditModal(true);
    };

    const handleEditModalSave = (updatedMedia: MediaModel) => {
      const mediaToDelete = mediaItems.find(
        (item) => item.mediaId === editingMedia?.mediaId
      );
      if (mediaToDelete && updatedMedia) {
        const updated = mediaItems.map((item) =>
          item.mediaId === mediaToDelete.mediaId ? updatedMedia : item
        );
        fundraiserStore.updateBuilderField("mediaItems", updated);
      }
    };

    const handleSaveModal = () => {
      if (editingMedia) {
        updateMediaItem(
          editingMedia.mediaId,
          new MediaModel({
            ...editingMedia,
            title: modalTitle,
            description: modalDescription,
          })
        );
      }
      setShowModal(false);
      setEditingMedia(null);
      setModalTitle("");
      setModalDescription("");
    };

    const handleCloseModal = () => {
      setShowModal(false);
      setEditingMedia(null);
      setModalTitle("");
      setModalDescription("");
    };

    const handleRotateToggle = (enabled: boolean) => {
      fundraiserStore.updateBuilderField("rotateMedia", enabled);
    };

    const handleDragStart = (e: React.DragEvent, id: string) => {
      e.stopPropagation();

      // Create a custom drag image
      const dragElement = e.currentTarget.closest(
        "[data-media-item]"
      ) as HTMLElement;
      if (dragElement) {
        const clone = dragElement.cloneNode(true) as HTMLElement;
        clone.style.transform = "rotate(5deg) scale(1.1)";
        clone.style.opacity = "0.9";
        clone.style.position = "absolute";
        clone.style.top = "-1000px";
        clone.style.left = "-1000px";
        clone.style.zIndex = "9999";
        document.body.appendChild(clone);

        // Use the clone as drag image
        e.dataTransfer.setDragImage(clone, 100, 70);

        // Clean up the clone after a short delay
        setTimeout(() => {
          document.body.removeChild(clone);
        }, 0);
      }

      setDraggedItem(id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    };

    const handleDragEnd = (e: React.DragEvent) => {
      e.stopPropagation();
      setDraggedItem(null);
      setDragOverItem(null);
      setDropPosition(null);
    };

    const handleDragOver = (
      e: React.DragEvent,
      targetId: string,
      index: number
    ) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";

      if (draggedItem && draggedItem !== targetId) {
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        const mouseX = e.clientX;

        // Determine if we're dropping before or after the current item
        const position = mouseX < midpoint ? "before" : "after";

        setDragOverItem(targetId);
        setDropPosition(position);
      }
    };

    // Add drag over handler for the entire grid to catch drops at the very end
    const handleGridDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (draggedItem && mediaItems.length > 0) {
        const gridRect = e.currentTarget.getBoundingClientRect();
        const lastItem = mediaItems[mediaItems.length - 1];

        // Check if we're dragging beyond the last item
        const mouseX = e.clientX;
        const gridRight = gridRect.right - 100; // Account for padding/margin

        if (mouseX > gridRight) {
          setDragOverItem(lastItem.mediaId);
          setDropPosition("after");
        }
      }
    };
    const handleDragLeave = (e: React.DragEvent) => {
      e.stopPropagation();
      // Only clear if we're leaving the media item container
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragOverItem(null);
        setDropPosition(null);
      }
    };

    const handleDrop = (
      e: React.DragEvent,
      targetId: string,
      index: number
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const draggedId = e.dataTransfer.getData("text/plain");
      if (!draggedId) return;

      const draggedIndex = mediaItems.findIndex(
        (item) => item.mediaId === draggedId
      );
      if (draggedIndex === -1) return;

      let targetIndex = index;

      // Adjust drop position based on where the mouse was
      if (dropPosition === "after") {
        targetIndex = index + 1;
      }

      // Don't do anything if dropping in the same position
      if (
        draggedIndex === targetIndex ||
        (draggedIndex === targetIndex - 1 && dropPosition === "after")
      ) {
        setDraggedItem(null);
        setDragOverItem(null);
        setDropPosition(null);
        return;
      }

      const reordered = [...mediaItems];
      const [draggedMedia] = reordered.splice(draggedIndex, 1);

      // Calculate the correct insertion index after removing the dragged item
      let insertIndex = targetIndex;

      // If we removed an item before the target position, adjust the index
      if (draggedIndex < targetIndex) {
        insertIndex = targetIndex - 1;
      }

      // Clamp the index to valid bounds (0 to array length)
      insertIndex = Math.max(0, Math.min(insertIndex, reordered.length));

      reordered.splice(insertIndex, 0, draggedMedia);

      // Update order values
      const updated = reordered.map((item, index) => ({
        ...item,
        order: index,
      }));
      fundraiserStore.updateBuilderField("mediaItems", updated);

      setDraggedItem(null);
      setDragOverItem(null);
      setDropPosition(null);
    };

    // Add drop handler for the grid itself
    const handleGridDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // If we're dropping on the grid but not on a specific item, drop at the end
      if (draggedItem && mediaItems.length > 0) {
        const lastItem = mediaItems[mediaItems.length - 1];
        const lastIndex = mediaItems.findIndex(
          (item) => item.mediaId === lastItem.mediaId
        );

        if (lastIndex !== -1) {
          setDropPosition("after");
          handleDrop(e, lastItem.mediaId, lastIndex);
        }
      }
    };
    // const moveMedia = (fromIndex: number, toIndex: number) => {
    //   const updated = [...mediaItems];
    //   const [moved] = updated.splice(fromIndex, 1);
    //   updated.splice(toIndex, 0, moved);

    //   // Update order values
    //   updated.forEach((item, index) => {
    //     item.order = index;
    //   });

    //   fundraiserStore.updateBuilderField("mediaItems", updated);
    // };

    const getVideoEmbedUrl = (url: string) => {
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    };

    return (
      <MediaContainer>
        <MediaGrid onDragOver={handleGridDragOver} onDrop={handleGridDrop}>
          {mediaItems.map((item, index) => (
            <MediaItem
              key={item.mediaId}
              data-media-item
              isDragging={draggedItem === item.mediaId}
              isDropTarget={dragOverItem === item.mediaId}
              onDragOver={(e) => handleDragOver(e, item.mediaId, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, item.mediaId, index)}
            >
              <DropZone
                isActive={
                  dragOverItem === item.mediaId &&
                  dropPosition === "before" &&
                  draggedItem !== item.mediaId
                }
                position="before"
              />

              <HoverOverlay className="hover-overlay">
                <HoverActions>
                  <HoverButton
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.mediaId)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => e.stopPropagation()}
                    title="Move"
                  >
                    <GripVertical size={18} />
                  </HoverButton>
                  <HoverButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(item);
                    }}
                    title="Edit"
                  >
                    <Edit2 size={18} />
                  </HoverButton>
                  <HoverButton
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMedia(item.mediaId);
                    }}
                    title="Remove"
                  >
                    <X size={18} />
                  </HoverButton>
                </HoverActions>
              </HoverOverlay>

              <MediaPreview>
                {item.type === MediaType.Image ? (
                  <MediaImage
                    src={item.fullURL}
                    alt={item.title || "Media"} 
                  />
                ) : (
                  <MediaVideo
                    src={getVideoEmbedUrl(item.fullURL)}
                    title={item.title || "Video"}
                  />
                )}
              </MediaPreview>

              <DropZone
                isActive={
                  dragOverItem === item.mediaId &&
                  dropPosition === "after" &&
                  draggedItem !== item.mediaId
                }
                position="after"
              />

              <MediaInfo>
                <MediaTitle>{item.title || "Untitled"}</MediaTitle>
                <MediaDescription>
                  {item.description || "No description"}
                </MediaDescription>
              </MediaInfo>
            </MediaItem>
          ))}

          {/* Add an invisible drop zone at the very end for last position drops */}
          {mediaItems.length > 0 && draggedItem && (
            <div
              style={{
                position: "absolute",
                right: "-20px",
                top: 0,
                bottom: 0,
                width: "40px",
                pointerEvents: "auto",
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedItem) {
                  const lastItem = mediaItems[mediaItems.length - 1];
                  setDragOverItem(lastItem.mediaId);
                  setDropPosition("after");
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedItem && mediaItems.length > 0) {
                  const lastItem = mediaItems[mediaItems.length - 1];
                  const lastIndex = mediaItems.findIndex(
                    (item) => item.mediaId === lastItem.mediaId
                  );
                  if (lastIndex !== -1) {
                    setDropPosition("after");
                    handleDrop(e, lastItem.mediaId, lastIndex);
                  }
                }
              }}
            />
          )}

          <AddMediaButton
            hasError={hasError}
            onClick={() =>
              fundraiserStore.updateBuilderField(
                "showMediaSectionLibrary",
                true
              )
            }
          >
            <Plus size={32} />
            <AddMediaText>Add Media</AddMediaText>
            <AddMediaSubtext>
              Select or upload an image or video
            </AddMediaSubtext>
          </AddMediaButton>
        </MediaGrid>

        <RotateMediaSection
          visible={mediaItems.length > 1}
          style={{ marginTop: "0px" }}
        >
          <RotateLabel>
            <RotateCheckbox
              type="checkbox"
              checked={rotateMedia}
              onChange={(e) => handleRotateToggle(e.target.checked)}
            />
            Rotate media automatically
          </RotateLabel>
        </RotateMediaSection>

        <FileInput
          id="file-input"
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFileUpload(e.target.files)}
          onKeyDown={(e) => {
            if (onKeyDown) {
              onKeyDown(e, true);
            }
          }}
        />

        {showEditModal && editingMedia?.fullURL && (
          <EditMediaModal
            isVisible={showEditModal}
            mediaItem={editingMedia}
            onClose={() => {
              setShowEditModal(false);
              setEditingMedia(null);
            }}
            onSave={handleEditModalSave}
            showDetailsPanel={true}
          />
        )}

        {showModal &&
          createPortal(
            <Modal visible={showModal}>
              <ModalContent>
                <ModalHeader>
                  <ModalTitle>Edit Media Details</ModalTitle>
                  <CloseButton onClick={handleCloseModal}>
                    <X size={20} />
                  </CloseButton>
                </ModalHeader>

                {editingMedia && (
                  <>
                    <ModalMediaPreview>
                      {editingMedia.type === MediaType.Image ? (
                        <ModalMediaImage
                          src={editingMedia.fullURL}
                          alt={editingMedia.title || "Media"} 
                        />
                      ) : (
                        <ModalMediaVideo
                          src={getVideoEmbedUrl(editingMedia.fullURL)}
                          title={editingMedia.title || "Video"}
                        />
                      )}
                    </ModalMediaPreview>

                    <FormGroup>
                      <Label>Title</Label>
                      <InputContainer>
                        <Input
                          type="text"
                          value={modalTitle}
                          onChange={(e) => setModalTitle(e.target.value)}
                          placeholder="Enter media title..."
                        />
                        <ClearButton
                          visible={!!modalTitle}
                          onClick={() => setModalTitle("")}
                          type="button"
                        >
                          <X size={12} />
                        </ClearButton>
                      </InputContainer>
                    </FormGroup>

                    <FormGroup>
                      <Label>Description</Label>
                      <TextAreaContainer>
                        <TextArea
                          value={modalDescription}
                          onChange={(e) => setModalDescription(e.target.value)}
                          placeholder="Enter media description..."
                        />
                        <TextAreaClearButton
                          visible={!!modalDescription}
                          onClick={() => setModalDescription("")}
                          type="button"
                        >
                          <X size={12} />
                        </TextAreaClearButton>
                      </TextAreaContainer>
                    </FormGroup>

                    <ModalActions>
                      <ModalButton onClick={handleCloseModal}>
                        Cancel
                      </ModalButton>
                      <ModalButton variant="primary" onClick={handleSaveModal}>
                        Save Changes
                      </ModalButton>
                    </ModalActions>
                  </>
                )}
              </ModalContent>
            </Modal>,
            document.body
          )}
      </MediaContainer>
    );
  }
);
